import React from 'react';
import { Plus } from 'lucide-react';
import { ActionItem, EditValues, NewCardValues, TaskStatus } from '../types';
import TaskCard from './TaskCard';
import TaskForm from './TaskForm';

interface Founder {
  id: string;
  name: string;
}

interface BoardColumnProps {
  title: string;
  status: TaskStatus;
  items: ActionItem[];
  creatingCard: string | null;
  newCardValues: NewCardValues;
  setNewCardValues: React.Dispatch<React.SetStateAction<NewCardValues>>;
  editingCard: string | null;
  editValues: EditValues;
  setEditValues: React.Dispatch<React.SetStateAction<EditValues>>;
  draggedItem: string | null;
  allFounders: Founder[];
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, status: TaskStatus) => void;
  onDragStart: (e: React.DragEvent, itemId: string) => void;
  onDragEnd: () => void;
  onCardDragOver: (e: React.DragEvent, targetItemId: string) => void;
  onCreateClick: (status: string) => void;
  onCreateSave: () => void;
  onCreateCancel: () => void;
  onEditClick: (item: ActionItem) => void;
  onEditSave: (itemId: string) => void;
  onEditCancel: () => void;
  onDelete: (itemId: string) => void;
  hasEdits: (item: ActionItem) => boolean;
  onFounderClick?: (founderId: string) => void;
  showReminder?: boolean;
}

const BoardColumn: React.FC<BoardColumnProps> = ({
  title,
  status,
  items,
  creatingCard,
  newCardValues,
  setNewCardValues,
  editingCard,
  editValues,
  setEditValues,
  draggedItem,
  allFounders,
  onDragOver,
  onDrop,
  onDragStart,
  onDragEnd,
  onCardDragOver,
  onCreateClick,
  onCreateSave,
  onCreateCancel,
  onEditClick,
  onEditSave,
  onEditCancel,
  onDelete,
  hasEdits,
  onFounderClick,
  showReminder = true,
}) => {
  const statusKey = status === 'pending' ? 'pending' : 'completed';

  return (
    <div className="trello-column" onDragOver={onDragOver} onDrop={(e) => onDrop(e, status)}>
      <div className="column-header">
        <div className="column-title-wrapper">
          <h2>{title}</h2>
        </div>
        <button
          className="add-card-button"
          onClick={() => onCreateClick(statusKey)}
          title="Add new task"
        >
          <Plus size={16} />
        </button>
      </div>
      <div className="column-cards">
        {creatingCard === statusKey && (
          <div className="trello-card editing">
            <TaskForm
              values={newCardValues}
              setValues={setNewCardValues}
              onSave={onCreateSave}
              onCancel={onCreateCancel}
              showReminder={showReminder}
            />
          </div>
        )}
        {items.map((item) => (
          <TaskCard
            key={item.id}
            item={item}
            isEditing={editingCard === item.id}
            editValues={editValues}
            setEditValues={setEditValues}
            hasEdits={hasEdits(item)}
            draggedItem={draggedItem}
            allFounders={allFounders}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onCardDragOver={onCardDragOver}
            onEditClick={onEditClick}
            onEditSave={onEditSave}
            onEditCancel={onEditCancel}
            onDelete={onDelete}
            onFounderClick={onFounderClick}
          />
        ))}
      </div>
    </div>
  );
};

export default BoardColumn;
